import React, { useEffect, useMemo, useState } from "react";
import Select from "react-select";
import { VendorCloseSvg, VendorShareSvg } from "../../../components/CommonSvg";
import { SHARE_WITH_VENDOR_USER } from "./documentShareApi";
import { fileKind, fileSize } from "./documentUtils";

const MENU_ON_TOP = {
    menuPortal: (base) => ({ ...base, zIndex: 1100 }),
};

const ALL_VENDORS_OPTION = [{ value: "all", label: "All Vendors" }];

const UserName = ({ option }) => (
    <span className="vp-share-opt-name">
        <b title={option.label}>{option.label}</b>
        {option.email && <i title={option.email}>{option.email}</i>}
    </span>
);

const renderOption = (option) => (
    <span className="vp-share-opt">
        <UserName option={option} />
    </span>
);

const ShareDocumentModal = ({
    document,
    shareWith,
    users = [],
    sharedWith = [],
    loading = false,
    sharing = false,
    onLoadTargets,
    onSubmit,
    onShared,
    onCancel,
}) => {
    const adminSide = shareWith === SHARE_WITH_VENDOR_USER;

    /* Deliberately empty to begin with. A useState initialiser runs once, on
       mount, and at that point sharedWith is still [] - the targets are only
       fetched in an effect below. Seeding from it here read a document's
       existing audience as "nothing", and nothing ever put it right. Both are
       filled in by the sync effect once the response lands. */
    const [allVendors, setAllVendors] = useState(false);
    const [pickedVendors, setPickedVendors] = useState([]);
    const [picked, setPicked] = useState([]);
    const [saving, setSaving] = useState(false);

    const kind = fileKind(document?.documentName);
    const size = fileSize(document?.fileSize);

    const userOptions = useMemo(
        () =>
            users.map((user) => ({
                value: String(user.id),
                label: user.name,
                email: user.email || "",
            })),
        [users]
    );

    const optionByValue = useMemo(() => {
        const map = new Map();
        userOptions.forEach((option) => map.set(option.value, option));
        return map;
    }, [userOptions]);

    useEffect(() => {
        onLoadTargets({ documentId: document?.documentId });
    }, [document?.documentId]);

    /* What the document is already shared with, applied once the API has said
       so. touched guards it: after the user has changed the selection their
       choice wins, or a late response would undo what they just did. */
    const touched = React.useRef(false);
    useEffect(() => {
        if (touched.current) return;

        if (adminSide) {
            /* the admin side stores an empty share as "every vendor" */
            setAllVendors(sharedWith.length === 0);
            setPickedVendors(sharedWith.map((who) => Number(who.id)));
            return;
        }

        setPicked(sharedWith.map((who) => String(who.id)));
    }, [sharedWith, adminSide]);

    const pickedOptions = useMemo(
        () => picked.map((key) => optionByValue.get(key)).filter(Boolean),
        [picked, optionByValue]
    );

    const removeUser = (key) => {
        touched.current = true;
        setPicked((current) => current.filter((held) => held !== key));
    };

    const handleShare = async () => {
        setSaving(true);
        try {
            const result = adminSide
                ? await onSubmit({
                      documentId: document?.documentId,
                      allVendors: allVendors,
                      vendorIds: allVendors ? [] : pickedVendors,
                  })
                : await onSubmit({
                      documentId: document?.documentId,
                      userIds: pickedOptions.map((option) => Number(option.value)),
                  });

            if (result?.status) onShared();
        } finally {
            setSaving(false);
        }
    };

    const busy = sharing || saving;
    const canSubmit = !busy && !loading;

    return (
        <div className="vp-share">
            <div className="vp-share-doc">
                <span className={`vp-share-doc-type ${kind.cls}`}>{kind.label}</span>
                <span className="vp-share-doc-text">
                    <b title={document?.documentName}>{document?.documentName}</b>
                    <span className="vp-share-doc-meta">
                        <span>{kind.type}</span>
                        {size && <span>{size}</span>}
                        {document?.createdDate && <span>{document.createdDate}</span>}
                    </span>
                </span>
            </div>

            <div className="vp-share-step">
                <label className="vp-share-step-head">
                    {adminSide ? "Visible To Vendors" : "Admin Users "}
                </label>

                {/* Nothing is rendered until the targets are in. The picker's
                    value is the document's current audience, and drawing it
                    from a response that has not arrived shows an empty or
                    half-filled selection first and then rearranges it under
                    the user - which reads as the dialog losing their data. */}
                {loading ? (
                    <div className="vp-share-loading">
                        Loading current access…
                    </div>
                ) : adminSide ? (
                    <Select
                        inputId="vp-share-vendors"
                        classNamePrefix="vp-share-sel"
                        isMulti
                        placeholder="All Vendors, or search for specific vendors…"
                        isLoading={loading}
                        menuPlacement="auto"
                        maxMenuHeight={260}
                        menuPortalTarget={window.document.body}
                        styles={MENU_ON_TOP}
                        options={ALL_VENDORS_OPTION.concat(
                            users.map((vendor) => ({ value: vendor.id, label: vendor.name }))
                        )}
                        value={
                            allVendors
                                ? ALL_VENDORS_OPTION
                                : users
                                      .filter((vendor) =>
                                          pickedVendors
                                              .map(String)
                                              .includes(String(vendor.id))
                                      )
                                      .map((vendor) => ({ value: vendor.id, label: vendor.name }))
                        }
                        onChange={(chosen) => {
                            touched.current = true;
                            const list = chosen || [];
                            const hasAll = list.some((option) => option.value === "all");

                            if (hasAll && (!allVendors || list.length === 1)) {
                                setAllVendors(true);
                                setPickedVendors([]);
                            } else {
                                setAllVendors(false);
                                setPickedVendors(
                                    list
                                        .filter((option) => option.value !== "all")
                                        .map((option) => option.value)
                                );
                            }
                        }}
                    />
                ) : (
                    <>
                        <Select
                            inputId="vp-share-users"
                            classNamePrefix="vp-share-sel"
                            isMulti
                            controlShouldRenderValue={false}
                            isClearable={false}
                            hideSelectedOptions
                            closeMenuOnSelect={false}
                            backspaceRemovesValue={false}
                            menuPlacement="auto"
                            maxMenuHeight={260}
                            menuPortalTarget={window.document.body}
                            styles={MENU_ON_TOP}
                            isLoading={loading}
                            placeholder="Search users by name or email"
                            noOptionsMessage={() => {
                                if (userOptions.length === 0) {
                                    return "Nobody here can be shared with yet";
                                }
                                if (pickedOptions.length === userOptions.length) {
                                    return "Everyone here is already selected";
                                }
                                return "No match";
                            }}
                            options={userOptions}
                            value={pickedOptions}
                            formatOptionLabel={renderOption}
                            filterOption={(option, input) => {
                                if (!input) return true;
                                const term = input.toLowerCase();
                                return (
                                    String(option.data.label).toLowerCase().includes(term) ||
                                    String(option.data.email).toLowerCase().includes(term)
                                );
                            }}
                            onChange={(chosen) => {
                                touched.current = true;
                                setPicked((chosen || []).map((option) => option.value));
                            }}
                        />

                        <div className="vp-share-step-foot">
                            <span>
                                <b>{pickedOptions.length}</b>
                                {userOptions.length > 0 && ` of ${userOptions.length}`}
                                {pickedOptions.length === 1 ? " user" : " users"} selected
                            </span>

                            {pickedOptions.length > 0 && (
                                <button
                                    type="button"
                                    className="vp-share-clear"
                                    onClick={() => {
                                        touched.current = true;
                                        setPicked([]);
                                    }}
                                >
                                    Remove all
                                </button>
                            )}
                        </div>

                        <div className="vp-share-picked">
                            {pickedOptions.length === 0 ? (
                                <p className="vp-share-none">
                                    {sharedWith.length > 0
                                        ? "Nobody selected - saving now withdraws this document."
                                        : "Nobody selected yet. Search above to add people."}
                                </p>
                            ) : (
                                pickedOptions.map((option) => (
                                    <div className="vp-share-row" key={option.value}>
                                        <UserName option={option} />
                                        <button
                                            type="button"
                                            className="vp-share-x"
                                            title={`Remove ${option.label}`}
                                            onClick={() => removeUser(option.value)}
                                        >
                                            <VendorCloseSvg size={12} />
                                        </button>
                                    </div>
                                ))
                            )}
                        </div>
                    </>
                )}
            </div>

            <div className="vp-share-foot">
                <div className="vp-share-btns">
                    <button type="button" className="vp-doc-cancel" onClick={onCancel}>
                        Cancel
                    </button>
                    <button
                        type="button"
                        className="vp-submit-btn"
                        disabled={!canSubmit}
                        onClick={handleShare}
                    >
                        <VendorShareSvg size={14} />
                        {busy ? "Sharing…" : "Share Document"}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default ShareDocumentModal;
